import { useMemo } from 'react'

import {
  TicketingResolverTeamsDocument,
  type TicketingResolverTeamsQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useMyTeams, type TeamOption } from 'modules/ticketing/hooks/useMyTeams'

/**
 * Returns only the resolver teams that the current user is a member of.
 * This is used in Insights to scope the team filter to teams the user
 * can actually act on, rather than every team in the org.
 */
export const useMyResolverTeams = (): {
  teams: TeamOption[]
  loading: boolean
  error?: unknown
} => {
  const { teams: myTeams, loading: loadingMyTeams, error: myTeamsError } = useMyTeams()

  const {
    data: resolverTeamsData,
    loading: loadingResolverTeams,
    error: resolverTeamsError,
  } = useQuery<TicketingResolverTeamsQuery, Record<string, never>>(TicketingResolverTeamsDocument, {
    fetchPolicy: 'cache-first',
  })

  const teams = useMemo<TeamOption[]>(() => {
    const resolverTeamIds = new Set(
      (resolverTeamsData?.ticketing?.resolverTeamsConnection?.nodes ?? []).map((n) =>
        String(n.teamId)
      )
    )

    return myTeams.filter((t) => resolverTeamIds.has(t.value))
  }, [myTeams, resolverTeamsData])

  return {
    teams,
    loading: loadingMyTeams || loadingResolverTeams,
    error: myTeamsError ?? resolverTeamsError,
  }
}
